import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { CategoryPills, PriceRangePills } from '../../../../components/filters';

export const FiltersModal = ({ isOpen, onClose, filters, onApply, categories = [] }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const priceRange = { min: localFilters.priceMin || 0, max: localFilters.priceMax || Infinity };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white w-full sm:w-96 sm:rounded-2xl rounded-t-2xl p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-800">Filters</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-3">Gender</p>
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setLocalFilters({ ...localFilters, gender: 'female' })}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                localFilters.gender === 'female' ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Women
            </button>
            <button
              onClick={() => setLocalFilters({ ...localFilters, gender: 'male' })}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                localFilters.gender === 'male' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Men
            </button>
          </div>
        </div>

        {categories.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-3">Category</p>
            <CategoryPills
              categories={categories}
              value={localFilters.category}
              onChange={(val) => setLocalFilters({ ...localFilters, category: val })}
              showAll={true}
            />
          </div>
        )}

        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-3">Search by Name</p>
          <input
            type="text"
            value={localFilters.searchQuery || ''}
            onChange={(e) => setLocalFilters({ ...localFilters, searchQuery: e.target.value })}
            placeholder="Search dress name or SKU..."
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-3">Price Range</p>
          <PriceRangePills
            value={priceRange}
            onChange={(val) => setLocalFilters({ ...localFilters, priceMin: val.min, priceMax: val.max })}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setLocalFilters({ gender: 'female', priceMin: 0, priceMax: Infinity, category: '', searchQuery: '' })}
            className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
          >
            Reset
          </button>
          <button
            onClick={() => { onApply(localFilters); onClose(); }}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Apply
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
